import { animated, IntersectionArgs, useInView } from '@react-spring/web';
import { Modal, Pressable, StyleSheet, Text, View } from 'react-native';
import { useNavigate } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import {
  IThemeContext,
  useCredentialData,
  useThemeContext,
  useWebSocketContext
} from '../../contexts';
import CardSuccessIcon from '../../components/Icons/CardSuccessIcon';
import Header from '../../components/layout/Header';
import AnimationFactory from '../../components/animation/AnimationFactory';
import { CredentialProofRequest } from '../../types/credentials';

interface ProofSuccessProps {
  proof: CredentialProofRequest;
}
/**
 * ProofSuccess is a component that displays a success message after a proof request has been sent.
 *
 * @return {React.FC}
 */
const ProofSuccess: React.FC<ProofSuccessProps> = ({ proof }) => {
  const navigate = useNavigate();
  const { theme } = useThemeContext();
  const { lastMessage } = useWebSocketContext();
  const [complete, setComplete] = useState(false);
  const styles = createProofSuccessStyles(theme);
  const { removeProof } = useCredentialData();

  useEffect(() => {
    if (lastMessage?.data === 'process-complete') {
      setComplete(true);
    }
  }, [lastMessage]);

  useEffect(() => {
    return () => {
      removeProof(proof);
    };
  }, []);

  const [iconAnim, iconAnimStyle] = useInView(AnimationFactory.getAnimation('fadeUp'), [
    complete
  ] as IntersectionArgs);

  const [textAnim, textAnimStyle] = useInView(AnimationFactory.withDelay('fadeUp', 100), [
    complete
  ] as IntersectionArgs);

  const [buttonAnim, buttonAnimStyle] = useInView(AnimationFactory.withDelay('fadeUp', 200), [
    complete
  ] as IntersectionArgs);

  return (
    <Modal>
      <Header title="Information Sent"></Header>
      {complete ? (
        <View style={styles.emptyListStyle}>
          <animated.div ref={iconAnim} style={iconAnimStyle}>
            <CardSuccessIcon iconHeight={160} iconWidth={160} />
          </animated.div>
          <View>
            <animated.div ref={textAnim} style={textAnimStyle}>
              <View style={styles.emptyListTextStyle}>
                <Text style={styles.emptyListMessageTitleStyle}>Information Sent!</Text>
                <Text style={styles.emptyListMessageSubtitleStyle}>
                  Check back with the requester.
                </Text>
              </View>
            </animated.div>
          </View>

          <View style={{ marginTop: 16 }}>
            <animated.div ref={buttonAnim} style={buttonAnimStyle}>
              <Pressable
                style={styles.buttonStyle}
                role="button"
                onPress={() => {
                  navigate('/');
                }}>
                <Text style={styles.buttonText}>Back to Wallet</Text>
              </Pressable>
            </animated.div>
          </View>
        </View>
      ) : (
        <View style={styles.emptyListStyle}>
          <animated.div ref={iconAnim} style={iconAnimStyle}>
            {AnimationFactory.createAnimation('proof')}
          </animated.div>
          <View>
            <animated.div ref={textAnim} style={textAnimStyle}>
              <View style={styles.emptyListTextStyle}>
                <Text style={styles.emptyListMessageTitleStyle}>Sending information...</Text>
                <Text style={styles.emptyListMessageSubtitleStyle}>please wait</Text>
              </View>
            </animated.div>
          </View>
        </View>
      )}
    </Modal>
  );
};
export default ProofSuccess;

const createProofSuccessStyles = (theme: IThemeContext['theme']) =>
  StyleSheet.create({
    emptyListStyle: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 36,
      flex: 1,
      width: '100%',
      height: '100%'
    },
    emptyListTextStyle: {
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      flex: 1
    },
    emptyListMessageTitleStyle: {
      fontSize: 24,
      fontWeight: 700,
      textAlign: 'center'
    },
    emptyListMessageSubtitleStyle: {
      fontSize: 18,
      color: '#999',
      textAlign: 'center'
    },
    onboardButton: {
      paddingTop: 16,
      paddingRight: 24,
      paddingBottom: 16,
      paddingLeft: 24,
      backgroundColor: 'rgb(41, 45, 50)',
      borderRadius: 6,
      color: '#fff',
      textAlign: 'center',
      boxShadow: '1px 2px 4px rgba(41, 45, 50, 0.5)'
    },
    onboardButtonText: {
      color: 'inherit',
      fontSize: 16
    },
    buttonStyle: {
      paddingTop: 16,
      paddingRight: 24,
      paddingBottom: 16,
      paddingLeft: 24,
      borderRadius: 6,
      backgroundColor: theme.primary,
      color: '#fff',
      textAlign: 'center',
      boxShadow: '1px 2px 4px rgba(41, 45, 50, 0.5)'
    },
    buttonText: {
      textAlign: 'center',
      color: 'inherit',
      width: 350,
      fontSize: 16,
      fontWeight: 600
    }
  });
